/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetJsonWebKeySetRequest,
  GetJsonWebKeySetSecurity,
} from "../models/operations/getjsonwebkeyset.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildGetJsonWebKeySetQuery,
  GetJsonWebKeySetQueryData,
  prefetchGetJsonWebKeySet,
  queryKeyGetJsonWebKeySet,
} from "./getJsonWebKeySet.core.js";
export {
  buildGetJsonWebKeySetQuery,
  type GetJsonWebKeySetQueryData,
  prefetchGetJsonWebKeySet,
  queryKeyGetJsonWebKeySet,
};

export type GetJsonWebKeySetQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getSet jsonWebKeySets
 *
 * @remarks
 * Get a JSON Web Key Set by ID. Requires org:read.
 */
export function useGetJsonWebKeySet(
  request: GetJsonWebKeySetRequest,
  security?: GetJsonWebKeySetSecurity | undefined,
  options?: QueryHookOptions<
    GetJsonWebKeySetQueryData,
    GetJsonWebKeySetQueryError
  >,
): UseQueryResult<GetJsonWebKeySetQueryData, GetJsonWebKeySetQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildGetJsonWebKeySetQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getSet jsonWebKeySets
 *
 * @remarks
 * Get a JSON Web Key Set by ID. Requires org:read.
 */
export function useGetJsonWebKeySetSuspense(
  request: GetJsonWebKeySetRequest,
  security?: GetJsonWebKeySetSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    GetJsonWebKeySetQueryData,
    GetJsonWebKeySetQueryError
  >,
): UseSuspenseQueryResult<
  GetJsonWebKeySetQueryData,
  GetJsonWebKeySetQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildGetJsonWebKeySetQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setGetJsonWebKeySetData(
  client: QueryClient,
  queryKeyBase: [parameters: { id: string; gramSession?: string | undefined }],
  data: GetJsonWebKeySetQueryData,
): GetJsonWebKeySetQueryData | undefined {
  const key = queryKeyGetJsonWebKeySet(...queryKeyBase);

  return client.setQueryData<GetJsonWebKeySetQueryData>(key, data);
}

export function invalidateGetJsonWebKeySet(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { id: string; gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "jsonWebKeySets", "get", ...queryKeyBase],
  });
}

export function invalidateAllGetJsonWebKeySet(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "jsonWebKeySets", "get"],
  });
}
